import { notEmpty } from "@product/scmp-sdk";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import type { ContentHeadlineVariant } from "~/components/content/content-headline";
import { MagazinesStyleTagsMap } from "~/components/content/content-headline/content-headline-tag/variants/magazines-style/const";
import type { contentHeadlineTagContent$key } from "~/queries/__generated__/contentHeadlineTagContent.graphql";

import { useDetermineHeadlineTag } from "./hooks";
import { Description, Flag, Heading, StyledTooltip } from "./styles";

export type Props = {
  hideTooltip?: boolean;
  reference: contentHeadlineTagContent$key;
  useSectionTags?: boolean;
  variant?: ContentHeadlineVariant;
};

export const ContentHeadlineTag: FunctionComponent<Props> = ({
  hideTooltip,
  reference: reference_,
  useSectionTags = false,
  variant,
}) => {
  const content = useFragment(
    graphql`
      fragment contentHeadlineTagContent on Content {
        ...hooksContentHeadlineTagContent
      }
    `,
    reference_,
  );
  const headlineTag = useDetermineHeadlineTag(content, useSectionTags);
  if (!headlineTag || !headlineTag.name) return null;

  const handleRenderFlag = () => {
    const handleRenderMagazinesStyleTags = () => {
      if (!headlineTag.entityId || variant !== "magazines-style") return null;
      return MagazinesStyleTagsMap[headlineTag.entityId];
    };

    return (
      <Flag $variant={variant} color={headlineTag.color}>
        {handleRenderMagazinesStyleTags() ?? headlineTag.name}
      </Flag>
    );
  };
  if (notEmpty(hideTooltip) || !headlineTag.description) return handleRenderFlag();

  return (
    <StyledTooltip
      placement="bottom-start"
      title={
        <>
          {headlineTag.name && <Heading>{headlineTag.name}</Heading>}
          {headlineTag.description && <Description>{headlineTag.description}</Description>}
        </>
      }
      arrow
    >
      {handleRenderFlag()}
    </StyledTooltip>
  );
};

ContentHeadlineTag.displayName = "ContentHeadlineTag";
